import * as Sentry from "@sentry/vue";
import { useAppVersionCheck as usePgAppVersionCheck } from "@polargold/pg-frontend-vue";
import { useAppVersionStore } from "@/stores/appVersionStore";

interface VersionResponse {
    version: string;
}

/**
 * Thin wrapper pinning this app's `/version.json` polling endpoint and `appVersionStore` onto
 * `pg-frontend-vue`'s `useAppVersionCheck`. `intervalMs` isn't passed - the default (300000, 5
 * minutes) already matches this app's original `AppVersionCheck.IntervalDuration`.
 */
export const useAppVersionCheck = () => {
    const appVersionStore = useAppVersionStore();

    const getCurrentVersion = async (): Promise<string | undefined> => {
        try {
            const response = await fetch(`/version.json?t=${Date.now()}`, {
                cache: "no-store",
            });

            const data: VersionResponse = await response.json();

            return data.version;
        } catch (err: unknown) {
            Sentry.captureException(err);

            return undefined;
        }
    };

    return usePgAppVersionCheck({
        getCurrentVersion,
        getLoadedVersion: () => appVersionStore.loadedVersion,
        setLoadedVersion: appVersionStore.setLoadedVersion,
        isUpdateAlreadyFlagged: () => appVersionStore.updateAvailable,
        onUpdateAvailable: appVersionStore.flagUpdateAvailable,
    });
};
